import { Analytics } from '@cobliteam/analytics';
import type { UserAttributes } from '@cobliteam/api-lib';

import type { Session, UserData } from '@cobliteam/common-types';
import { UserRole } from '@cobliteam/common-types';
// root package.json
import datadogRUM from '@cobliteam/datadog-rum';
import type { FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import { Notifications } from '@cobliteam/notifications';
import { Environment } from '@cobliteam/react-environments';
import { clarity } from 'react-microsoft-clarity';
import packageJSON from '../../../../package.json';

const env = Environment.getInstance().getEnvironment();

export const configureOneSignal = ({ user, fleet }: Session) => {
  const isLocalhost = window.location.href.includes('localhost');
  if (!isLocalhost) Notifications.initialize(fleet.id, fleet.name, user.id);
};

export const configureAnalytics = (session: Session, featureFlags: FeatureFlags, userAttributes?: UserAttributes | null) => {
  Analytics.setUser(session, featureFlags, userAttributes);
};

export const configureDatadogRUM = (session: Session, sampleRate: number, replaySampleRate: number, userAttributes?: UserAttributes | null) => {
  const isLocalhost = window.location.href.includes('localhost');
  const isPreview = window.location.href.includes('dashboard.preview.cobli.co');

  // when running locally or in preview, we don't want to send any data to datadog
  const envSampleRate = isLocalhost || isPreview ? 0 : sampleRate;
  const envReplaySampleRate = isLocalhost || isPreview ? 0 : replaySampleRate;

  const configuration = {
    service: 'herbie-dashboard',
    env: env.name,
    version: packageJSON.version,
    sessionSampleRate: envSampleRate,
    sessionReplaySampleRate: envReplaySampleRate,
    trackResources: true,
    trackLongTasks: true,
    traceIntegration: true,
    trackFrustrations: true,
    trackUserInteractions: true,
  };

  const user = {
    id: session?.user?.id,
    name: session?.user?.name,
    email: session?.user?.email,
    role: session?.user?.role,
    app_version: configuration.version,
    fleet: {
      id: session?.fleet?.id,
      name: session?.fleet?.name,
      num_devices: userAttributes?.numberOfDevices,
      creation_date: userAttributes?.creationDate,
      age_in_days: userAttributes?.ageInDays,
    },
  };

  datadogRUM({ configuration, user });
};

export const configureClarity = ({ user, fleet }: Session, featureFlags: FeatureFlags) => {
  if (env.clarityId) {
    clarity.init(env.clarityId);

    if (clarity.hasStarted()) {
      clarity.setTag('fleet_id', fleet.id);
      clarity.setTag('fleet_name', fleet.name);
      clarity.setTag('user_id', user.id);
      clarity.setTag('user_email', user.email);
      clarity.setTag('version', window.__DASHBOARD_MONOREPO_VERSION__);
      clarity.setTag('sidebar', featureFlags.sidebar ? 'true' : 'false');
      clarity.setTag('ai-chat', featureFlags.aiChat ? 'true' : 'false');
      clarity.identify(user.id, {
        email: user.email,
        username: user.name,
        fleet_id: fleet.id,
        fleet_name: fleet.name,
        version: window.__DASHBOARD_MONOREPO_VERSION__,
      });

      clarity.upgrade('Session started with data user');
    }
  }
};

export const initializeThirdParty = ({ session, userData, featureFlags }: { session: Session; userData?: UserData; featureFlags: FeatureFlags }) => {
  const userAttributes = userData?.userAttributes;

  // analytics segment
  configureAnalytics(session, featureFlags, userAttributes);

  // oneSignal
  if (session.user.role !== UserRole.MAP_VIEW && featureFlags.oneSignal) {
    configureOneSignal(session);
  }

  // datadogRUM
  if (featureFlags.useDdRum && featureFlags.ddRumSampleRate) {
    configureDatadogRUM(session, featureFlags.ddRumSampleRate, featureFlags.ddRumReplaySampleRate, userAttributes);
  }

  // clarity
  configureClarity(session, featureFlags);
};
